/**
 * Custom storage adapter for Supabase that uses Capacitor Preferences on native platforms.
 * This ensures session persistence on iOS, where localStorage can be cleared when the app closes.
 *
 * Concurrency: on iPadOS 26 / WebKit, simultaneous Preferences.get/set calls under
 * React.StrictMode double-mount can race and (rarely) hang. We serialize all
 * Preferences calls through a single promise queue and bound each call with a
 * 2s timeout so a stuck native bridge call can never block app boot.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isNative = Capacitor.isNativePlatform();

let prefsQueue = Promise.resolve();
const withPrefsLock = (fn) => {
  const next = prefsQueue.then(() => withTimeout(fn(), 2000)).catch((err) => {
    console.warn('[capacitorStorage] queued op failed:', err);
    return null;
  });
  prefsQueue = next.catch(() => {});
  return next;
};

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
  ]);

/**
 * Supabase-compatible storage adapter.
 * Uses Capacitor Preferences (native storage) on iOS/Android,
 * falls back to localStorage on web.
 */
export const capacitorStorage = {
  async getItem(key) {
    if (isNative) {
      return withPrefsLock(async () => {
        try {
          const { value } = await Preferences.get({ key });
          return value;
        } catch (err) {
          console.error('[capacitorStorage] getItem error:', err);
          return null;
        }
      });
    }
    return localStorage.getItem(key);
  },

  async setItem(key, value) {
    if (isNative) {
      return withPrefsLock(async () => {
        try {
          await Preferences.set({ key, value });
        } catch (err) {
          console.error('[capacitorStorage] setItem error:', err);
        }
      });
    }
    localStorage.setItem(key, value);
  },

  async removeItem(key) {
    if (isNative) {
      return withPrefsLock(async () => {
        try {
          await Preferences.remove({ key });
        } catch (err) {
          console.error('[capacitorStorage] removeItem error:', err);
        }
      });
    }
    localStorage.removeItem(key);
  },
};
